package ai.jessie.web.work

import ai.jessie.shared.work.WorkInvoiceStatus
import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// UC-008 management for owners and standard members. `workId` is null on the list page. The form
// opens as a slide-over above the list or the record (`docs/design/screens/04-Stunden.png`).
@Composable
fun workRecords(organisationId: String, workId: String?, state: WorkUiState, actions: WorkActions) {
    val focusReturn = remember { FocusReturn() }
    workFocus(state, focusReturn)
    val editing = state.draft != null
    if (!editing) workMessages(state)
    if (state.error?.authenticationRequired != true)
        workBody(organisationId, workId, state, actions)
    if (editing) {
        val title =
            if (workId != null && state.current != null) "Stunden bearbeiten"
            else "Stunden erfassen"
        workEditor(state, actions, close = actions.cancel, title = title)
    }
    if (workId != null && state.current == null)
        P { A(href = "/organisations/$organisationId/work") { Text(BACK_TO_LIST) } }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

// While the slide-over is open it shows these itself.
@Composable
private fun workMessages(state: WorkUiState) {
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.busy) status("Wird geladen oder gespeichert …")
    state.notice?.let { status(it) }
}

@Composable
private fun workBody(
    organisationId: String,
    workId: String?,
    state: WorkUiState,
    actions: WorkActions,
) {
    val current = state.current
    when {
        current != null -> {
            workRecord(current, state.selections, showDate = workId == null)
            if (workId == null) savedActions(organisationId, current, state, actions)
            else entryActions(organisationId, state, actions)
        }
        workId == null -> workList(organisationId, state, actions)
        else ->
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(RELOAD_ENTRY, actions.refresh, disabled = state.busy)
            }
    }
}

// Every view change replaces the control that had focus, so the page title takes it again; while
// a problem is shown, the alert or the marked field keeps focus. The first render leaves the
// frame's own title focus alone.
@Composable
private fun workFocus(state: WorkUiState, focusReturn: FocusReturn) {
    LaunchedEffect(state.draft == null, state.current == null) {
        val settled = state.draft == null && state.error == null
        if (focusReturn.pending && settled && !AppGlobals.isExporting)
            (document.getElementById("page-title") as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

@Composable
private fun status(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
private fun savedActions(
    organisationId: String,
    current: WorkResponse,
    state: WorkUiState,
    actions: WorkActions,
) {
    Div(attrs = RowStyle.toAttrs()) {
        linkButton(
            "Arbeitseintrag öffnen",
            "/organisations/$organisationId/work/${current.id}",
            variant = ButtonVariant.PRIMARY,
        )
        themedButton("Zur Arbeitsliste", actions.close, disabled = state.busy)
    }
}

@Composable
private fun entryActions(organisationId: String, state: WorkUiState, actions: WorkActions) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            "Bearbeiten",
            actions.edit,
            variant = ButtonVariant.PRIMARY,
            disabled = state.busy,
        )
        linkButton(BACK_TO_LIST, "/organisations/$organisationId/work")
        themedButton(
            RELOAD_ENTRY,
            actions.refresh,
            variant = ButtonVariant.GHOST,
            disabled = state.busy,
        )
    }
}

internal data class WorkReferences(val member: String, val project: String, val position: String)

// Names come only from the current permitted choices; a deactivated reference stays unnamed.
internal fun workReferences(
    entry: WorkResponse,
    selections: WorkSelectionsResponse?,
): WorkReferences {
    val member = selections?.members?.firstOrNull { it.id == entry.memberId }
    val project = selections?.projects?.firstOrNull { it.id == entry.projectId }
    val position = project?.positions?.firstOrNull { it.id == entry.positionId }
    return WorkReferences(
        member = member?.let { memberLabel(it.name) } ?: NOT_SELECTABLE,
        project = project?.name ?: NOT_SELECTABLE,
        position = position?.name ?: NOT_SELECTABLE,
    )
}

internal fun memberLabel(name: String?) = name ?: "Mitglied ohne Namen"

internal fun minutesLabel(minutes: Int) = "$minutes Min."

// B1: an unavailable integration is never presented as unlinked or unprotected work.
internal fun invoiceLabel(status: WorkInvoiceStatus) =
    when (status) {
        WorkInvoiceStatus.UNAVAILABLE ->
            "Verrechnungsstatus: steht in Jessie zurzeit nicht zur Verfügung. Das ist keine " +
                "Aussage darüber, ob diese Arbeit verrechnet ist."
        WorkInvoiceStatus.UNBILLED -> "Verrechnungsstatus: nicht verrechnet"
        WorkInvoiceStatus.DRAFT -> "Verrechnungsstatus: in einem Rechnungsentwurf"
        WorkInvoiceStatus.ISSUED -> "Verrechnungsstatus: verrechnet"
    }

internal const val RELOAD_ENTRY = "Arbeitseintrag erneut laden"
internal const val BACK_TO_LIST = "Zurück zur Liste"
private const val NOT_SELECTABLE = "nicht mehr wählbar"
